import AppPagination from "@/components/AppPagination";
import WrapWithCopy from "@/components/WrapWithCopy.vue";
import TableWithTitle from "@/components/TableWithTitle.vue";
import WrapWithPopover from "@/components/shared/WrapWithPopover.vue";
import { getDescriptionBasedOnKpiKey, getColorForDecile, getColorsBasedOnRange, transformGoogleSheetFromCSV } from "@/utils/transformers";
import { SELECTOR, LTV_PRODUCT_FIELD, LTV_PER_ASIN_NEW_CUSTOMER, LTV_PER_ASIN_MULTIPLIER, LTV_PER_ASIN_BREAKDOWN } from "@/utils/table_fields";
import AppIcon from "@/components/AppIcon.vue";
import { mapState, mapActions } from "vuex";
import { HugValue, DateValue, RateChangeValue, CurrencyValue, NumberValue, BoxedValue, BarWithValue, PercentageValue, DottedValue, OverlapBarValue, EvolutionArrow, SegmentedBar, ImageElement, HashedNumberValue, DottedPositionBar, DashedPositionBar, BarWithDifferenceFromMaxMarked, RangeBar, DotElement, ValueElement } from "@/components/tableElements";
import LiteTableMixin from "@/mixins/LiteTableMixin.vue";
import DownloadMixin from "@/mixins/DownloadMixin.vue";
import TasksMixin from "@/mixins/TasksMixin.vue";
import { DEFAULT_DATE_FORMATS, DAYS_INVENTORY_HEATMAP_COLORS, ORGANIC_CVR_HEATMAP_COLORS, BUYBOX_HEATMAP_COLORS, PERIOD_COLUMN_TITLES, COLORS, APP_LTV_DROPDOWN_ELEMENTS, CUSTOMER_INSIGHTS_LTV_COLORS } from "@/utils/constants";
import FlexRow from "@/components/FlexRow.vue";
import moment from "moment";
import { capitalize } from "vue";
import SlotWithEvolutionValue from "@/components/tableElements/SlotWithEvolutionValue.vue";
import EvolutionValue from "@/components/tableElements/EvolutionValue.vue";
import WrapTableWithShowMore from "@/components/WrapTableWithShowMore.vue";
import TextValue from "@/components/tableElements/TextValue.vue";
import LTVCompositeBar from "@/components/customer_insights/LTVCompositeBar.vue";
import HoverableImage from "@/components/HoverableImage.vue";
import AppInput from "@/components/AppInput";
import { EVENTS } from "@/utils";
export default {
  name: "LTVPerAsinTable",
  components: {
    TableWithTitle,
    WrapWithPopover,
    AppIcon,
    HugValue,
    DateValue,
    RateChangeValue,
    CurrencyValue,
    NumberValue,
    BoxedValue,
    BarWithValue,
    PercentageValue,
    DottedValue,
    OverlapBarValue,
    FlexRow,
    SlotWithEvolutionValue,
    EvolutionValue,
    WrapTableWithShowMore,
    TextValue,
    EvolutionArrow,
    SegmentedBar,
    ImageElement,
    HashedNumberValue,
    DottedPositionBar,
    DashedPositionBar,
    BarWithDifferenceFromMaxMarked,
    RangeBar,
    DotElement,
    ValueElement,
    WrapWithCopy,
    LTVCompositeBar,
    HoverableImage,
    AppPagination,
    AppInput
  },
  mixins: [LiteTableMixin, DownloadMixin, TasksMixin],
  props: {
    loading: {
      typ: Boolean,
      required: true
    }
  },
  emits: ["setDateRange", "refresh"],
  data() {
    return {
      search: "",
      CUSTOMER_INSIGHTS_LTV_COLORS,
      DAYS_INVENTORY_HEATMAP_COLORS,
      ORGANIC_CVR_HEATMAP_COLORS,
      BUYBOX_HEATMAP_COLORS,
      PERIOD_COLUMN_TITLES,
      COLORS,
      fields: [LTV_PRODUCT_FIELD],
      sortableFields: [LTV_PER_ASIN_NEW_CUSTOMER, LTV_PER_ASIN_MULTIPLIER, LTV_PER_ASIN_BREAKDOWN],
      currency_ltv_options: [APP_LTV_DROPDOWN_ELEMENTS.LTV.value, APP_LTV_DROPDOWN_ELEMENTS.REPEAT_LTV.value, APP_LTV_DROPDOWN_ELEMENTS.REPEAT_AOV.value],
      percentage_ltv_options: [APP_LTV_DROPDOWN_ELEMENTS.REPURCHASE_RATE.value],
      number_ltv_options: [APP_LTV_DROPDOWN_ELEMENTS.REPURCHASE_FREQUENCY.value],
      evolCategory: {
        prev_period: "customers_in_cohort_evol_pp",
        same_period_ly: "customers_in_cohort_evol_yoy"
      },
      csvSources: period => ({
        Asin: "asin",
        "Product title": "product_name",
        "Entry customer 12 months": "cohort_customers",
        Currency: "currency",
        "LTV multiplier revenue": "ltv_multiplier_revenue_excl_vat",
        "Avg LTV revenue": "brand_weighted_ltv_revenue_excl_vat",
        "First purchase revenue": "brand_first_purchase_weighted_ltv_revenue_excl_vat",
        "Entry repeat revenue": "entry_asin_repeat_weighted_ltv_revenue_excl_vat",
        "Cross repeat revenue": "non_entry_asin_repeat_weighted_ltv_revenue_excl_vat",
        "LTV multiplier margin": "ltv_multiplier_margin",
        "Avg LTV margin": "brand_weighted_ltv_margin",
        "First purchase margin": "brand_first_purchase_weighted_ltv_margin",
        "Entry repeat margin": "entry_asin_repeat_weighted_ltv_margin",
        "Cross repeat margin": "non_entry_asin_repeat_weighted_ltv_margin"
      }),
      hoverableImage: null,
      fieldValues: {
        ltv_per_asin_new_customers: "cohort_customers",
        ltv_per_asin_multiplier: "multiplier",
        ltv_per_asin_breakdown: "total_value"
      },
      sort_key: "ltv_per_asin_new_customers",
      sortType: -1
    };
  },
  computed: {
    ...mapState({
      periodTable: state => state.customer_insights.periodTable,
      tab: state => state.customer_insights.currentTabValue,
      period: state => state.customer_insights.period,
      currentPeriod: state => state.customer_insights.currentPeriod,
      periodCategory: state => state.customer_insights.periodCategory,
      ltv: state => state.customer_insights.ltv,
      currentMetricValue: state => state.customer_insights.currentMetricValue,
      total_rows: state => state.customer_insights.total_rows,
      page: state => state.customer_insights.page_offset,
      perPage: state => state.customer_insights.page_limit,
      currencies: state => state.customer_insights.currencies,
      current_workspace: state => state.workspace.current_workspace,
      me: state => state.auth.me
    }),
    computedFields() {
      const computedSortableFields = this.sortableFields.map(field => {
        return {
          ...field,
          sortable: field.key == this.sort_key
        };
      });
      return [...this.fields, ...computedSortableFields];
    },
    items() {
      if (this.periodTable[this.tab].items.length == 0) {
        window.$bus.trigger("showDanger", {
          visible: true,
          message: "No cohort data available yet",
          delay: 6000,
          isHtml: true
        });
      }
      let computed_items = this.periodTable[this.tab].items.map(row => ({
        ...row,
        currency: this.currencies.selectedValues[0],
        multiplier: this.currentMetricValue == "revenue" ? row.ltv_multiplier_revenue_excl_vat : row.ltv_multiplier_margin,
        total_value: this.currentMetricValue == "revenue" ? row.brand_weighted_ltv_revenue_excl_vat : row.brand_weighted_ltv_margin,
        first_purchase: this.currentMetricValue == "revenue" ? row.brand_first_purchase_weighted_ltv_revenue_excl_vat : row.brand_first_purchase_weighted_ltv_margin,
        entry_repeat: this.currentMetricValue == "revenue" ? row.entry_asin_repeat_weighted_ltv_revenue_excl_vat : row.entry_asin_repeat_weighted_ltv_margin,
        cross_repeat: this.currentMetricValue == "revenue" ? row.non_entry_asin_repeat_weighted_ltv_revenue_excl_vat : row.non_entry_asin_repeat_weighted_ltv_margin
      }));
      if (this.sort_key != "") {
        const sortField = this.fieldValues[this.sort_key];
        computed_items.sort((a, b) => {
          let sort_result = (a?.[sortField] ?? 0) - (b?.[sortField] ?? 0);
          return sort_result * this.sortType;
        });
      }
      return computed_items;
    },
    calculatedMultiplierColor() {
      return item => {
        return item.multiplier < 1.1 ? "#FFF6F0" : item.multiplier < 1.5 ? "#FEC9AA" : "#f89053";
      };
    },
    breakdownValue() {
      return item => {
        return [{
          text: "First purchase",
          value: item.first_purchase ?? 0,
          color: "#ADCDFF"
        }, {
          text: "Entry repeat",
          value: item.entry_repeat ?? 0,
          color: "#FCAF83"
        }, {
          text: "Cross repeat",
          value: item.cross_repeat ?? 0,
          color: "#FFDCC7"
        }];
      };
    },
    maxCharCount() {
      return this.periodTable[this.tab].maxCharCount;
    },
    minMax() {
      let minmax = this.periodTable[this.tab].minMax;
      let multiplierArr = this.items.map(item => item.multiplier);
      let multiplier = {
        min: multiplierArr.length ? Math.min(...multiplierArr) : null,
        max: multiplierArr.length ? Math.max(...multiplierArr) : null
      };
      let totalValueArr = this.items.map(item => item.total_value);
      let total_value = {
        min: totalValueArr.length ? Math.min(...totalValueArr) : null,
        max: totalValueArr.length ? Math.max(...totalValueArr) : null
      };
      return {
        ...minmax,
        multiplier,
        total_value
      };
    },
    getDateValueFormat() {
      // Convert the current period to UPPER CASE
      const period = this.period.toUpperCase();
      return DEFAULT_DATE_FORMATS[period];
    },
    getPeriodColumnTitle() {
      return PERIOD_COLUMN_TITLES[this.period];
    },
    period_pager: {
      get() {
        return this.periodTable.period_page;
      },
      set(_) {
        this.$store.dispatch("customerInsightsIncrementPeriodPage");
        this.$emit("refresh");
      }
    },
    customerNumberFormat() {
      return num => {
        if (num >= 1_000) return (num / 1000).toFixed(1) + "K";
        return num;
      };
    },
    multiplierFormat() {
      return num => {
        return num ? num.toFixed(2) + "x" : "-";
      };
    },
    computedMinMax() {
      let valueArray = this.items.flatMap(item => this.ltvOffset.map(offset => item.kpis?.[this.ltv]?.["m" + offset.split("_")[2]]));
      console.log("valueArray", valueArray);
      const numbers = valueArray.filter(v => typeof v === "number");
      return {
        min: Math.min(...numbers),
        max: Math.max(...numbers)
      };
    },
    csvFileName() {
      return `Customer_Insights_LTV_Per_ASIN_${capitalize(this.me.email)}_${capitalize(this.current_workspace.workspace_id)}.csv`;
    }
  },
  mounted() {
    this.customerInsightsKeyWordListSet([]);
  },
  methods: {
    ...mapActions(["customerInsightsTotalRowsSet", "customerInsightsPageLimitSet", "customerInsightsPageOffsetSet", "customerInsightsKeyWordListSet"]),
    getDescriptionBasedOnKpiKey,
    getColorForDecile,
    prepareItems(items) {
      return items.map(row => {
        if (!row.kpis) return row;
        try {
          const parsed = JSON.parse(row.kpis);
          row.kpis = (parsed.kpis || []).reduce((acc, item) => {
            acc[item.kpi_name] = item.months;
            return acc;
          }, {});
        } catch {
          console.warn("Invalid kpis JSON");
        }
        return row;
      });
    },
    async changePage(newPage) {
      this.customerInsightsPageOffsetSet(newPage);
      await this.$store.dispatch("customerInsightsFetchPeriodTableData");
    },
    async changeElements(elementsPerPage) {
      this.customerInsightsPageOffsetSet(1);
      this.customerInsightsPageLimitSet(elementsPerPage);
      await this.$store.dispatch("customerInsightsFetchPeriodTableData");
    },
    onSort(field) {
      const selected_key = field.key;
      if (this.sort_key === selected_key) {
        this.sortType = -this.sortType;
      } else {
        this.sort_key = selected_key;
        this.sortType = 1;
      }
    },
    async searchItem($event) {
      const value = $event.split(",").filter(v => !!v).map(v => String(v).trim());
      this.customerInsightsKeyWordListSet(value);
      await window.$bus.trigger(EVENTS.REFRESH_CUSTOMER_INSIGHTS);
    },
    handleDateReset() {
      this.$emit("setDateRange", null);
    },
    handleDateClick(period) {
      period = moment(period);
      this.$emit("setDateRange", period);
    },
    handleRowClick(item) {
      this.handleRowClicked(item, this.handleDateReset, this.handleDateClick, "period_agg");
    },
    async downloadLTVPerAsinCSV() {
      const csv_headers = this.csvSources(this.period);
      const csvFileName = this.csvFileName;
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.downloadCsv(str, csvFileName);
      this.trackDashboardCsvExport();
    },
    async copyLTVPerAsinCSV() {
      const csv_headers = this.csvSources(this.period);
      const str = await this.generateCsvString(csv_headers, this.prepareCsvRowsForDownload(this.items, csv_headers), 1, "-");
      await this.copyCSV(str);
    },
    async downloadLTVPerAsinGoogleSheet() {
      const csv_headers = this.csvSources(this.period);
      const rowData = this.prepareCsvRowsForDownload(this.items, csv_headers);
      const data = transformGoogleSheetFromCSV(csv_headers, rowData);
      const task = await this.downloadCSVWithGoogleSheets("/customer_insights/ltvperasin/generate/googlesheet", {
        sheet_name: this.csvFileName,
        task_type: "google_sheet_generation",
        data
      });

      //console.log('dashboard task', task);

      if (!task) {
        return;
      }
      this.sheetGenerationTaskIdentifier = task.task_id;
      this.executeTaskStatusHandler(task);
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleRunning() {
      this.checkTaskStatusPeriodically(this.sheetGenerationTaskIdentifier);
    },
    handleFailed(data) {
      this.exportLoading = false;
      if (this.asyncUpdateInterval) {
        clearTimeout(this.asyncUpdateInterval);
        this.asyncUpdateInterval = null;
      }
      window.$bus.trigger("showDanger", {
        visible: true,
        message: "Google sheet generation Task failed \n" + data.task_result,
        delay: 60 // Show alert less than 60 secs
      });
      throw new Error("Google sheet generation Task failed");
    },
    async handleFinished(data) {
      //console.log('dashboard finished', data);
      if (data) {
        const {
          task_result: {
            share_link
          }
        } = data;
        if (this.asyncUpdateInterval) {
          clearTimeout(this.asyncUpdateInterval);
          this.asyncUpdateInterval = null;
        }
        window.$bus.trigger("showSuccess", {
          visible: true,
          message: "The google sheet has been successfully generated. A link has been sent to your email.",
          delay: 60 // Show alert less than 60 secs
        });
        this.exportLoading = false;
        if (share_link) window.open(share_link, "_blank");
      }
    }
  }
};